/**
 * Провайдеры (§43, §51).
 *
 * Включение и приоритет меняются без деплоя. Изменение влияет на роутинг
 * новых задач; уже отправленные доживают своим чередом.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { adminApi } from '../../api/admin';
import { Button } from '../../components/ui/Button';
import { ProviderCredentials } from '../../components/admin/ProviderCredentials';
import { formatTime } from '../../i18n/format';
import { useTranslation } from 'react-i18next';

export function AdminProvidersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openKeys, setOpenKeys] = useState<string | null>(null);

  const providers = useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: adminApi.providers,
    refetchInterval: 20_000,
  });

  const update = useMutation({
    mutationFn: (params: { id: string; input: Record<string, unknown> }) =>
      adminApi.updateProvider(params.id, params.input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] }),
  });

  return (
    <div className="overflow-x-auto rounded-card border border-line">
      <table className="w-full border-collapse text-meta">
        <thead>
          <tr className="border-b border-line bg-surface-secondary text-left text-content-secondary">
            <th className="px-3 py-2 font-medium">{t('admin.kind')}</th>
            <th className="px-3 py-2 font-medium">{t('admin.job.provider')}</th>
            <th className="px-3 py-2 font-medium">{t('admin.provider.state')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('admin.provider.priority')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('admin.provider.jobsDay')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('admin.provider.failures')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('admin.provider.avg')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('admin.job.cost')}</th>
            <th className="px-3 py-2 font-medium">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {(providers.data ?? []).flatMap((row) => [
            <tr key={row.id} className="border-b border-line bg-surface last:border-0">
              <td className="px-3 py-2">{row.kind}</td>
              <td className="px-3 py-2 text-content">{row.name}</td>
              <td className="px-3 py-2">
                <div className="flex flex-col gap-0.5">
                  <span className={row.isEnabled ? 'text-content' : 'text-content-muted'}>
                    {row.isEnabled ? t('admin.provider.enabled') : t('admin.provider.disabled')}
                  </span>
                  {/* Запись без адаптера в процессе — частая причина «включил,
                      а трафик не пошёл»: роутер её всё равно не выберет. */}
                  {!row.loaded ? <span className="text-warning">{t('admin.provider.noAdapter')}</span> : null}
                  {/* C7: без ключей провайдер в роутинг не попадёт вовсе. */}
                  {row.needsCredentials && !row.configured ? (
                    <span className="text-warning">{t('admin.provider.noKeys')}</span>
                  ) : null}
                  {row.circuitOpenUntil ? (
                    <span className="text-error">
                      {t('admin.provider.circuitUntil', { time: formatTime(row.circuitOpenUntil) })}
                    </span>
                  ) : null}
                </div>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                <input
                  type="number"
                  defaultValue={row.priority}
                  onBlur={(event) => {
                    const priority = Number(event.target.value);
                    if (priority !== row.priority) update.mutate({ id: row.id, input: { priority } });
                  }}
                  className="h-7 w-16 rounded-control border border-line bg-surface-secondary px-2 text-right text-meta text-content focus:border-accent focus:outline-none"
                />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{row.jobsLastDay}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${row.failuresLastDay > 0 ? 'text-error' : ''}`}>
                {row.failuresLastDay}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-content-secondary">
                {row.avgSeconds !== null ? t('admin.secondsShort', { value: row.avgSeconds }) : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-content-secondary">
                ${(row.costCentsLastDay / 100).toFixed(2)}
              </td>
              <td className="px-3 py-2">
                <div className="flex gap-2">
                  <Button
                    variant="tertiary"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: row.id, input: { isEnabled: !row.isEnabled } })}
                  >
                    {row.isEnabled ? t('admin.provider.disable') : t('admin.provider.enable')}
                  </Button>
                  {row.needsCredentials ? (
                    <Button
                      variant="tertiary"
                      onClick={() => setOpenKeys(openKeys === row.id ? null : row.id)}
                    >
                      <KeyRound className="h-3.5 w-3.5" strokeWidth={1.75} />{t('admin.provider.keys')}</Button>
                  ) : null}
                  {row.failureCount > 0 || row.circuitOpenUntil ? (
                    <Button
                      variant="tertiary"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: row.id, input: { resetCircuit: true } })}
                    >{t('admin.provider.reset')}</Button>
                  ) : null}
                </div>
              </td>
            </tr>,
            openKeys === row.id ? (
              <tr key={`${row.id}-keys`}>
                <td colSpan={9} className="p-0">
                  <ProviderCredentials
                    provider={row}
                    onSaved={() => queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] })}
                  />
                </td>
              </tr>
            ) : null,
          ])}
        </tbody>
      </table>
    </div>
  );
}
